<template>
    <div class="modal-mask">
        <div class="modal-wrapper">
            <div class="modal-container" style="width: 50%;">
                <div class="modal-header">
                    <slot name="header">
                        <h3 class="form-header">Clinician Bag Check In</h3>
                        <button @click="closeModal" class="red-close-button">
                            <i class="pi pi-times" style='padding: 0'></i>
                        </button>
                    </slot>
                </div>
                <div class="modal-body">
                    <slot name="body">
                        <form class="action-modal">
                            <div class="form-section-container" style="display: flex; width: 100%;">
                                <!-- LEFT PANEL: Info & Notes -->
                                <div class="left-form-section-panel" style="flex: 1; display: flex; flex-direction: column; padding-right: 20px; border-right: 1px solid #ccc;">
                                    <p style="text-align: left; font-weight: bold; margin-bottom: 5px;">Clinician Hold Registers <span style="color:red">*</span></p>
                                    <div style="margin-bottom: 15px;">
                                        <Dropdown
                                            v-model="searchSelection"
                                            :options="pendingCheckouts"
                                            optionLabel="display_name"
                                            style="width: 100%;"
                                            placeholder="Select a pending clinician hold..."
                                            @change="selectCheckout"
                                        />
                                        
                                        <div v-if="selectedCheckout" style="margin-top: 10px; padding: 10px; background-color: #f9f9f9; border-radius: 4px; border: 1px solid #eee;">
                                            <p style="margin: 0; font-size: 0.9em;"><strong>Date:</strong> {{ new Date(selectedCheckout.created_at).toLocaleString() }}</p>
                                            <p style="margin: 0; font-size: 0.9em;"><strong>Mission:</strong> {{ selectedCheckout.mission_number || 'N/A' }}</p>
                                            <p style="margin: 0; font-size: 0.9em;"><strong>Tag:</strong> {{ selectedCheckout.tag || 'N/A' }}</p>
                                            <p style="margin: 0; font-size: 0.9em;"><strong>Clinician:</strong> {{ selectedCheckout.clinician && selectedCheckout.clinician.personal_information ? selectedCheckout.clinician.personal_information.firstname + ' ' + selectedCheckout.clinician.personal_information.lastname : 'N/A' }}</p>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <p style="text-align: left; font-weight: bold; margin-bottom: 5px;">Notes</p>
                                        <textarea v-model="notes" rows="6" style="width: 100%; border-radius: 4px; padding: 8px;" placeholder="Optional check-in notes..."></textarea>
                                    </div>
                                </div>

                                <!-- RIGHT PANEL: Return Medications -->
                                <div class="right-form-section-panel" style="flex: 1; display: flex; flex-direction: column; padding-left: 20px;">
                                    <p style="text-align: left; font-weight: bold; margin-bottom: 5px;">Return Medications</p>
                                    <!-- Checking a bag in is one action and closes it, so the user
                                         has to know that before signing rather than after. -->
                                    <p style="text-align: left; font-size: 0.85em; color: #666; margin: 0 0 10px 0;">
                                        Checking in closes this bag. Enter what has come back - anything already given or destroyed stays recorded where it was.
                                    </p>
                                    <div class="right-form-section-drug-container" style="overflow-y: auto; max-height: 300px;">
                                        <table style="border-collapse: collapse; width: 100%;" v-if="checkinDrugs.length > 0">
                                            <tr>
                                                <th style="text-align: left; padding-bottom: 5px;">Drug</th>
                                                <th style="text-align: center; width: 60px;">Out</th>
                                                <th style="text-align: center; width: 80px;">Return</th>
                                            </tr>
                                            <tr v-for="(drug, index) in checkinDrugs" :key="index" style="border-bottom: 1px solid #eee;">
                                                <td style="padding: 10px 0; max-width: 250px; cursor: pointer;" 
                                                    @click="expandedDrugs.includes(index) ? expandedDrugs.splice(expandedDrugs.indexOf(index), 1) : expandedDrugs.push(index)"
                                                    :title="expandedDrugs.includes(index) ? 'Click to collapse' : 'Click to expand: ' + formatDrugName(drug.stock_item)">
                                                    <div :style="expandedDrugs.includes(index) ? { whiteSpace: 'normal' } : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }">
                                                        {{ formatDrugName(drug.stock_item) }}
                                                    </div>
                                                </td>
                                                <td style="text-align: center;">{{ drug.qty }}</td>
                                                <td style="text-align: center;">
                                                    <input type="number" v-model="drug.checkin_qty" min="0" :max="drug.qty" style="width: 75px; text-align: center;" />
                                                </td>
                                            </tr>
                                        </table>
                                        <p v-else style="color: #666; font-style: italic;">Select a hold to view medications.</p>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </slot>
                </div>
                <p style="color: red; font-weight: 500; font-size: 1em; text-align: center; margin-top: 10px;" v-if="errorMessage">{{ errorMessage }}</p>
                <div class="modal-footer" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
                    <slot name="footer">
                        <p class="regular-button error-button" style="color: white; margin-right: 10px;" @click="closeModal">Cancel</p>
                        <p class="regular-button green-button" @click="submitCheckin" v-if="selectedCheckout">Check In</p>
                    </slot>
                </div>
            </div>
        </div>
        <SignatureModal v-if="displaySignatureModal" :payload="realSubmit" @isSuccessful="isSuccessful" @responseData="captureCheckin" @close="close" :saveContext="saveContext" endpoint="/theatre-checkin" />
    </div>
</template>

<script>
import SignatureModal from '@/components/modals/SignatureModal.vue';
import axios from 'axios';
import { drugsRequireDoubleSig } from '@/services/doubleSignature';

export default {
    components: {
        SignatureModal
    },
    props: {
        preselectedCheckoutId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            loadingCheckouts: false,
            pendingCheckouts: [],
            searchSelection: null,
            selectedCheckout: null,
            checkinDrugs: [],
            expandedDrugs: [],
            notes: '',
            
            errorMessage: '',
            displaySignatureModal: false,
            realSubmit: null,
            saveContext: null,
            // The check in row kraken created, read off the response. What is recorded after the bag
            // is back points at it, so the administrations and discards belong to this check in.
            checkinUuid: null
        }
    },
    computed: {
        requiresDoubleSig() {
            const facility = this.$store.getters.getCurrentLocation?.facility;
            return drugsRequireDoubleSig(this.checkinDrugs.filter(d => d.checkin_qty > 0), facility);
        }
    },
    mounted() {
        this.fetchPendingCheckouts();
    },
    methods: {
        formatDrugName(stockItem) {
            if (!stockItem || !stockItem.alias) return 'Unknown Medication';
            const alias = stockItem.alias;
            const generic = alias.generic_name || (alias.generic_drug && alias.generic_drug.name);
            return generic ? `(${generic}) ${alias.name}` : alias.name;
        },
        closeModal() {
            this.$emit('close', true);
        },
        close(value) {
            if (value) {
                this.displaySignatureModal = false;
            }
        },
        fetchPendingCheckouts() {
            this.loadingCheckouts = true;
            axios.post('/get-open-clinician-holds', {}, this.$store.state.header).then(res => {
                const clinicianHolds = res.data.data || [];
                
                this.pendingCheckouts = clinicianHolds.map(checkout => {
                    let dateStr = new Date(checkout.created_at).toLocaleString();
                    let clinicianName = checkout.clinician && checkout.clinician.personal_information 
                        ? checkout.clinician.personal_information.firstname + ' ' + checkout.clinician.personal_information.lastname 
                        : 'Unknown';
                    let missionStr = checkout.mission_number ? ` - Mission: ${checkout.mission_number}` : '';
                    let tagStr = checkout.tag ? ` - Tag: ${checkout.tag}` : '';
                    checkout.display_name = `${dateStr} - Clinician: ${clinicianName}${missionStr}${tagStr}`;
                    return checkout;
                });
                this.loadingCheckouts = false;

                if (this.preselectedCheckoutId && !this.selectedCheckout) {
                    const match = this.pendingCheckouts.find(c => c.uuid === this.preselectedCheckoutId);
                    if (match) {
                        this.searchSelection = match;
                        this.selectCheckout();
                    }
                }
            }).catch(err => {
                this.loadingCheckouts = false;
                console.error("Failed to fetch pending checkouts", err);
            });
        },
        selectCheckout() {
            if (this.searchSelection) {
                this.selectedCheckout = this.searchSelection;
                this.checkinDrugs = [];
                JSON.parse(JSON.stringify(this.selectedCheckout.drugs)).forEach(d => {
                    // Calculate remaining quantity
                    let remaining = Number(d.quantity || d.qty || 0) - Number(d.checkin || 0) - Number(d.discard || 0) - Number(d.administered || 0);
                    if (remaining > 0) {
                        d.qty = remaining;
                        d.checkin_qty = 0;
                        this.checkinDrugs.push(d);
                    }
                });
                this.errorMessage = '';
            } else {
                this.errorMessage = "Please select a hold to proceed.";
            }
        },
        clearSelection() {
            this.selectedCheckout = null;
            this.checkinDrugs = [];
            this.searchSelection = null;
        },
        submitCheckin() {
            let invalidQty = false;

            const payloadDrugs = this.checkinDrugs.map(d => {
                const qty = Number(d.checkin_qty);
                if (qty < 0 || qty > d.qty) invalidQty = true;
                return {
                    drug: d.uuid,
                    checkin: qty,
                    administered: 0,
                    discard: 0,
                    tags: d.stock_item?.alias?.tags || []
                };
            });

            if (invalidQty) {
                this.errorMessage = "Return quantity cannot be negative or exceed the checked out amount.";
                return;
            }

            this.realSubmit = {
                uuid: this.selectedCheckout.uuid,
                theatreCheckout: this.selectedCheckout,
                drugs: payloadDrugs,
                notes: this.notes
            };

            this.saveContext = {
                type: 'ClinicianHoldCheckin',
                patient: `Mission: ${this.selectedCheckout.external_number} - Tag: ${this.selectedCheckout.tag}`,
                location: this.$store.state.currentLocation.name,
                drugs: payloadDrugs.filter(d => d.checkin > 0).map(d => ({ drug: 'Returned Item', qty: d.checkin })),
                doubleSig: this.requiresDoubleSig
            };

            this.errorMessage = '';
            this.displaySignatureModal = true;
        },
        captureCheckin(data) {
            // Laravel resources are wrapped, so the row arrives under `data`.
            const checkin = (data && data.data) ? data.data : data;
            this.checkinUuid = (checkin && checkin.uuid) ? checkin.uuid : null;
        },
        isSuccessful(value) {
            if (value) {
                // The mission and the check in travel with the close, so whoever opened this can
                // offer the administrations next without the user finding the bag again.
                this.$emit('close', true, 'success', {
                    checkinUuid: this.checkinUuid,
                    missionNumber: this.selectedCheckout ? this.selectedCheckout.mission_number : null
                });
            }
        }
    }
}
</script>

<style scoped>
/* Hide native spin buttons for number inputs */
input[type=number]::-webkit-inner-spin-button, 
input[type=number]::-webkit-outer-spin-button { 
  -webkit-appearance: none; 
  margin: 0; 
}
input[type=number] {
  -moz-appearance: textfield;
}
</style>
